import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Checkbox } from "../ui/checkbox";
import { NeonButton } from "../common";

export const buildLayers = (amount) => [
  { id: "non_refundable", text: "I understand this is a digital service delivered immediately and is non-refundable once accessed." },
  { id: "cardholder_auth", text: `I confirm I am the cardholder and I authorize this charge of $${Number(amount).toFixed(2)} to my card.` },
  { id: "terms", text: "I agree to the Terms of Service and refund policy (v2026.10) and to receive a post-payment confirmation with a 30-minute cancellation window." },
];

const b64 = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf)));
const sha256hex = async (s) => [...new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(s)))].map((x) => x.toString(16).padStart(2, "0")).join("");

async function signConsent({ layers, amount, email, typedName }) {
  const termsHash = await sha256hex(layers.map((l) => l.text).join("\n"));
  const keys = await crypto.subtle.generateKey({ name: "ECDSA", namedCurve: "P-256" }, true, ["sign", "verify"]);
  const payload = JSON.stringify({ terms_hash: termsHash, amount: Number(amount), email, typed_name: typedName, client_ts: new Date().toISOString() });
  const sig = await crypto.subtle.sign({ name: "ECDSA", hash: "SHA-256" }, keys.privateKey, new TextEncoder().encode(payload));
  return { public_key_jwk: await crypto.subtle.exportKey("jwk", keys.publicKey), client_signature: b64(sig), signed_payload: payload };
}

const deviceFingerprint = () => ({
  user_agent: navigator.userAgent, platform: navigator.platform, language: navigator.language,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone, screen: `${window.screen.width}x${window.screen.height}`,
  color_depth: window.screen.colorDepth, cpu_cores: navigator.hardwareConcurrency || null,
});

const SignaturePad = ({ onChange }) => {
  const ref = useRef(null);
  const drawing = useRef(false);
  useEffect(() => {
    const ctx = ref.current.getContext("2d");
    ctx.strokeStyle = "#CCFF00";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
  }, []);
  const pos = (e) => { const r = ref.current.getBoundingClientRect(); return [(e.clientX - r.left) * (460 / r.width), (e.clientY - r.top) * (120 / r.height)]; };
  const down = (e) => { drawing.current = true; const ctx = ref.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(...pos(e)); };
  const move = (e) => { if (!drawing.current) return; const ctx = ref.current.getContext("2d"); ctx.lineTo(...pos(e)); ctx.stroke(); };
  const up = () => { if (drawing.current) { drawing.current = false; onChange(ref.current.toDataURL("image/png")); } };
  const clear = () => { ref.current.getContext("2d").clearRect(0, 0, 460, 120); onChange(null); };
  return (
    <div>
      <canvas ref={ref} width={460} height={120} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up}
        className="w-full bg-canvas border border-dashed border-white/20 cursor-crosshair touch-none" data-testid="signature-pad" />
      <button onClick={clear} className="font-mono text-[10px] uppercase text-white/45 hover:text-white mt-1" data-testid="clear-signature-btn">clear</button>
    </div>
  );
};

export const ConsentModal = ({ open, onOpenChange, onConfirm, busy, amount, email, name }) => {
  const layers = buildLayers(amount);
  const [acks, setAcks] = useState({});
  const [typed, setTyped] = useState("");
  const [sig, setSig] = useState(null);
  const [err, setErr] = useState(null);
  const norm = (x) => (x || "").toLowerCase().split(/\s+/).filter(Boolean).join(" ");
  const nameOk = norm(typed) === norm(name);
  const ready = layers.every((l) => acks[l.id]) && nameOk && sig;

  const submit = async () => {
    setErr(null);
    try {
      const crypto = await signConsent({ layers, amount, email, typedName: typed.trim() });
      onConfirm({ layers: layers.map((l) => ({ ...l, accepted: true })), typed_name: typed.trim(), signature_image: sig, device: deviceFingerprint(), ...crypto });
    } catch (e) {
      setErr(`Browser signing failed: ${e.message}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-black/80 backdrop-blur-xl border-white/10 max-w-lg" data-testid="consent-modal">
        <DialogHeader>
          <DialogTitle className="font-mono tracking-tight">Litigation-grade consent</DialogTitle>
          <DialogDescription className="text-white/45">Each layer is hashed, signed in your browser (ECDSA P-256), server-timestamped and sealed with an RFC 3161 token.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {layers.map((l, i) => (
            <label key={l.id} className="flex items-start gap-3 p-3 border border-white/10 bg-white/[0.02] text-sm text-white/80 cursor-pointer" data-testid={`consent-layer-${l.id}`}>
              <Checkbox checked={!!acks[l.id]} onCheckedChange={(v) => setAcks({ ...acks, [l.id]: !!v })} data-testid={`consent-ack-${l.id}`} className="mt-0.5" />
              <span><span className="font-mono text-[10px] text-brand mr-2">L{i + 1}</span>{l.text}</span>
            </label>
          ))}
        </div>
        <div><p className="label-xs mb-1">Type your full name ({name})</p>
          <input value={typed} onChange={(e) => setTyped(e.target.value)} className="w-full bg-canvas border border-white/15 px-3 py-2 text-sm focus:outline-none focus:border-brand" data-testid="consent-typed-name" />
          {typed && !nameOk && <p className="text-[11px] text-[#FFB800] mt-1" data-testid="consent-name-mismatch">Must match the cardholder name exactly.</p>}</div>
        <div><p className="label-xs mb-2">Draw your signature</p><SignaturePad onChange={setSig} /></div>
        {err && <p className="text-xs text-[#F87171]" data-testid="consent-error">{err}</p>}
        <NeonButton disabled={!ready || busy} onClick={submit} className="w-full justify-center" data-testid="consent-sign-btn">
          {busy ? "Sealing…" : "Sign, seal & authorize payment"}
        </NeonButton>
      </DialogContent>
    </Dialog>
  );
};
